import {
  JOURNAL_BASE_PATH,
  getJournalTabs,
  normalizeJournalPath,
  parseJournalSectionPath,
  resolveJournalPage,
} from '~/shared/lib/journal-routes.const';
import { useJournalEditions } from './useJournalData';

/**
 * What the current /journal address shows: a tab, an edition of a tab, or the
 * media kit. Tabs are rows of the reference the editors keep, so the page can
 * only be told once the reference has arrived — it is awaited here.
 *
 * An address naming nothing is a 404, and the first tab answers at /journal
 * alone: its own slug redirects there, so the section never has two addresses
 * for its landing page
 */
export async function useJournalPage() {
  const route = useRoute();
  // Taken before the await: after it the Nuxt context is gone, and navigateTo
  // on the server throws instead of redirecting
  const nuxtApp = useNuxtApp();
  const { editions: reference } = await useJournalEditions();

  const target = computed(() =>
    resolveJournalPage(reference.value, route.path),
  );

  const notFound = () =>
    createError({ status: 404, message: 'Страница не найдена', fatal: true });

  const isFirstTabAlias = () => {
    const current = target.value;

    return (
      current?.kind === 'tab' &&
      current.tab.id === getJournalTabs(reference.value)[0]?.id &&
      normalizeJournalPath(route.path) !== JOURNAL_BASE_PATH
    );
  };

  if (!target.value) throw notFound();

  if (isFirstTabAlias()) {
    await nuxtApp.runWithContext(() =>
      navigateTo(JOURNAL_BASE_PATH, { redirectCode: 301, replace: true }),
    );
  }

  /**
   * Whether an address is this page's to answer. The watcher below also fires
   * on the way out — to an issue, a year or another section of the site — while
   * the page is still mounted, and those addresses name no tab: taking them for
   * a 404 turned every click on a cover into an error page
   */
  const isOwnAddress = (path: string) => {
    const normalized = normalizeJournalPath(path);

    if (
      normalized !== JOURNAL_BASE_PATH &&
      !normalized.startsWith(`${JOURNAL_BASE_PATH}/`)
    ) {
      return false;
    }

    const { kind } = parseJournalSectionPath(normalized);

    return kind !== 'issue' && kind !== 'year';
  };

  // The page stays mounted between addresses of the section, so a later
  // navigation is checked the same way the first one was
  watch(target, (value) => {
    if (!isOwnAddress(route.path)) return;

    if (!value) {
      showError(notFound());
    } else if (isFirstTabAlias()) {
      nuxtApp.runWithContext(() =>
        navigateTo(JOURNAL_BASE_PATH, { replace: true }),
      );
    }
  });

  return { reference, target };
}
